/**
 * `/instance/organizations`: every Organisation on this ELASO Instance, each with
 * its workspaces under it. A new one is created in its own flow,
 * at `/instance/organizations/new`.
 *
 * The guard, the socket claim and the shell live on the layout route
 * (`instance/route.tsx`), so this page owns only which procedures answer the
 * list and how long a repair is waited for.
 */
import type { OrganizationWorkspaceRow } from "@elaso/shared/types/organization";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ulid } from "ulid";
import { OrganizationList } from "@/components/instance/organization-list";
import { Button, buttonVariants } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import type { InstanceOrganizationRow } from "@/lib/instance-organization";
import { ws } from "@/lib/websocket-wrapper";

export const Route = createFileRoute("/instance/organizations/")({
	component: InstanceOrganizationsPage,
});

// A repair re-seeds the whole workspace against the permission service, as much
// work as an import, and on a workspace of ~500 participants it runs for
// minutes. On the WS wrapper's 30s default the page reported a repair that was
// still running as failed, and invited a second one on top of it (TECH-3378).
const RESEED_RPC_TIMEOUT_MS = 600_000;

type Listing = {
	organizations: InstanceOrganizationRow[];
	workspaces: OrganizationWorkspaceRow[];
};

function InstanceOrganizationsPage() {
	// `undefined` is "not answered yet", which is not the same answer as an empty
	// list, and a failed read must not read as "no Organisations yet".
	const [listing, setListing] = useState<Listing | undefined>(undefined);
	const [readFailed, setReadFailed] = useState(false);
	// Only the latest read may become the list: a reload after a repair or a
	// creation can overtake one still in flight.
	const latestRead = useRef(0);

	const load = useCallback(() => {
		latestRead.current += 1;
		const read = latestRead.current;
		setReadFailed(false);
		Promise.all([
			ws.sendWithPromise<{ organizations: InstanceOrganizationRow[] }>({
				id: ulid(),
				procedure: "instanceAdmin.listOrganizations",
				data: { includeDisabled: true },
			}),
			ws.sendWithPromise<{ workspaces: OrganizationWorkspaceRow[] }>({
				id: ulid(),
				procedure: "instanceAdmin.listWorkspaces",
				data: { includeDisabled: true },
			}),
		])
			.then(([{ organizations }, { workspaces }]) => {
				if (read === latestRead.current) {
					setListing({ organizations, workspaces });
				}
			})
			.catch(() => {
				if (read === latestRead.current) setReadFailed(true);
			});
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const handleRepair = useCallback(
		async (workspaceId: string) => {
			try {
				await ws.sendWithPromise({
					id: ulid(),
					procedure: "instanceAdmin.reseedWorkspace",
					data: { workspaceId },
					timeoutMs: RESEED_RPC_TIMEOUT_MS,
				});
				toast.success("Workspace repaired");
				load();
			} catch (err) {
				const message = err instanceof Error ? err.message : String(err);
				toast.error(`Repair failed: ${message}`);
				throw err;
			}
		},
		[load]
	);

	function renderListing() {
		if (readFailed) {
			return (
				<div className="flex items-center gap-3">
					<p className="text-muted-foreground text-sm">
						Could not load the Organisations.
					</p>
					<Button onClick={load} size="sm" type="button" variant="outline">
						Try again
					</Button>
				</div>
			);
		}
		if (listing === undefined) {
			return (
				<p className="text-muted-foreground text-sm">Loading Organisations…</p>
			);
		}
		if (listing.organizations.length === 0) {
			return (
				<p className="text-muted-foreground text-sm">No Organisations yet.</p>
			);
		}
		return (
			<OrganizationList
				onRepair={handleRepair}
				organizations={listing.organizations}
				workspaces={listing.workspaces}
			/>
		);
	}

	return (
		<div className="flex flex-col items-center gap-4 p-4 md:p-8">
			<Card className="w-full max-w-4xl">
				<CardHeader className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 px-4 sm:px-6">
					<div className="min-w-56 flex-1 space-y-1.5">
						<CardTitle>
							<h2>Organisations</h2>
						</CardTitle>
						<CardDescription>
							Every Organisation on this ELASO Instance, with its workspaces. An
							Organisation's name, adapter, administrators and workspaces are
							managed on its own page: Open it. A switched-off one is refused
							everywhere, its imports included.
						</CardDescription>
					</div>
					<Link
						className={buttonVariants({ size: "sm" })}
						to="/instance/organizations/new"
					>
						New Organisation
					</Link>
				</CardHeader>
				<CardContent className="space-y-6 px-4 sm:px-6">
					{renderListing()}
				</CardContent>
			</Card>
		</div>
	);
}
